/**
 * usePayoutMethods Hook
 *
 * Manages payout methods for host via Firestore
 * subcollection: users/{uid}/payoutMethods.
 */

import { useState, useEffect, useCallback } from 'react';
import {
  collection,
  doc,
  addDoc,
  updateDoc,
  deleteDoc,
  onSnapshot,
  query,
  orderBy,
  writeBatch,
  serverTimestamp,
} from 'firebase/firestore';
import { db } from '../../../../services/firebase';
import { useAuth } from '../../../../contexts';

export interface WhishMoneyDetails {
  phoneNumber: string;
  accountHolderName: string;
}

export interface WireTransferDetails {
  bankName: string;
  accountNumber: string;
  iban: string;
  swiftCode: string;
  beneficiaryName: string;
}

export interface PayoutMethod {
  id: string;
  type: 'whish_money' | 'wire_transfer';
  name: string;
  details: WhishMoneyDetails | WireTransferDetails;
  isDefault: boolean;
  createdAt: Date;
}

const toDate = (val: unknown): Date => {
  if (!val) return new Date();
  if (val instanceof Date) return val;
  if (typeof val === 'object' && 'toDate' in val && typeof (val as { toDate: () => Date }).toDate === 'function') {
    return (val as { toDate: () => Date }).toDate();
  }
  const d = new Date(val as string | number);
  return isNaN(d.getTime()) ? new Date() : d;
};

export const usePayoutMethods = () => {
  const { user } = useAuth();
  const [methods, setMethods] = useState<PayoutMethod[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const getCollectionRef = useCallback(() => {
    if (!user) return null;
    return collection(db, 'users', user.uid, 'payoutMethods');
  }, [user]);

  useEffect(() => {
    if (!user) {
      setMethods([]);
      setIsLoading(false);
      return;
    }

    const colRef = getCollectionRef();
    if (!colRef) return;

    const q = query(colRef, orderBy('createdAt', 'desc'));

    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        const items: PayoutMethod[] = snapshot.docs.map((d) => {
          const data = d.data();
          return {
            id: d.id,
            type: data.type || 'whish_money',
            name: data.name || '',
            details: data.details || {},
            isDefault: data.isDefault || false,
            createdAt: toDate(data.createdAt),
          } as PayoutMethod;
        });
        setMethods(items);
        setIsLoading(false);
      },
      (err) => {
        console.error('PayoutMethods snapshot error:', err);
        setError(err.message);
        setIsLoading(false);
      }
    );

    return () => unsubscribe();
  }, [user, getCollectionRef]);

  const addMethod = useCallback(
    async (method: Omit<PayoutMethod, 'id' | 'createdAt'>): Promise<boolean> => {
      const colRef = getCollectionRef();
      if (!colRef) return false;

      try {
        // If this is default or first method, clear other defaults
        if (method.isDefault || methods.length === 0) {
          const batch = writeBatch(db);
          for (const m of methods) {
            if (m.isDefault) {
              batch.update(doc(colRef, m.id), { isDefault: false });
            }
          }
          await batch.commit();
        }

        await addDoc(colRef, {
          ...method,
          isDefault: method.isDefault || methods.length === 0,
          createdAt: serverTimestamp(),
        });

        return true;
      } catch (err) {
        setError('Failed to add payout method');
        return false;
      }
    },
    [getCollectionRef, methods]
  );

  const updateMethod = useCallback(
    async (methodId: string, updates: Partial<PayoutMethod>): Promise<boolean> => {
      const colRef = getCollectionRef();
      if (!colRef) return false;

      try {
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const { id: _id, createdAt: _createdAt, ...safeUpdates } = updates as PayoutMethod;
        await updateDoc(doc(colRef, methodId), safeUpdates);
        return true;
      } catch (err) {
        setError('Failed to update payout method');
        return false;
      }
    },
    [getCollectionRef]
  );

  const deleteMethod = useCallback(
    async (methodId: string): Promise<boolean> => {
      const colRef = getCollectionRef();
      if (!colRef) return false;

      try {
        const wasDefault = methods.find((m) => m.id === methodId)?.isDefault;
        await deleteDoc(doc(colRef, methodId));

        // If we deleted the default, make the first remaining one default
        if (wasDefault) {
          const remaining = methods.filter((m) => m.id !== methodId);
          if (remaining.length > 0) {
            await updateDoc(doc(colRef, remaining[0].id), { isDefault: true });
          }
        }

        return true;
      } catch (err) {
        setError('Failed to delete payout method');
        return false;
      }
    },
    [getCollectionRef, methods]
  );

  const setDefaultMethod = useCallback(
    async (methodId: string): Promise<boolean> => {
      if (!user) return false;
      const colRef = getCollectionRef();
      if (!colRef) return false;

      try {
        const batch = writeBatch(db);
        for (const m of methods) {
          batch.update(doc(colRef, m.id), {
            isDefault: m.id === methodId,
          });
        }
        await batch.commit();
        return true;
      } catch (err) {
        setError('Failed to set default method');
        return false;
      }
    },
    [user, getCollectionRef, methods]
  );

  return {
    methods,
    isLoading,
    error,
    addMethod,
    updateMethod,
    deleteMethod,
    setDefaultMethod,
  };
};

export default usePayoutMethods;
